import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import { Users, Circle as CircleIcon, ArrowRightLeft, ShieldCheck } from 'lucide-react';
import api from '../api';

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      const { stats, recentActivity, revenueData } = response.data.data;
      setStats({
        totalUsers: stats.totalUsers,
        activeCircles: stats.activeCircles,
        totalTransactions: stats.totalTransactions,
        pendingKyc: stats.pendingKYC,
        revenueData: revenueData || [],
        recentUsers: recentActivity?.users || [],
      });
    } catch (err) {
      console.error(err);
      setStats({ totalUsers: 0, activeCircles: 0, totalTransactions: 0, pendingKyc: 0, revenueData: [], recentUsers: [] });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loader-container"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h1 className="page-title">Dashboard Overview</h1>

      <div className="grid-cards">
        <div className="stat-card glass" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '16px', borderRadius: '12px' }}>
            <Users size={32} color="var(--accent)" />
          </div>
          <div>
            <h3>Total Users</h3>
            <div className="value">{stats?.totalUsers || 0}</div>
          </div>
        </div>
        
        <div className="stat-card glass" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '16px', borderRadius: '12px' }}>
            <CircleIcon size={32} color="var(--success)" />
          </div>
          <div>
            <h3>Active Circles</h3>
            <div className="value">{stats?.activeCircles || 0}</div>
          </div>
        </div>

        <div className="stat-card glass" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ background: 'rgba(245, 158, 11, 0.1)', padding: '16px', borderRadius: '12px' }}>
            <ArrowRightLeft size={32} color="var(--warning)" />
          </div>
          <div>
            <h3>Transactions</h3>
            <div className="value">{stats?.totalTransactions || 0}</div>
          </div>
        </div>

        <div className="stat-card glass" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '16px', borderRadius: '12px' }}>
            <ShieldCheck size={32} color="var(--danger)" />
          </div>
          <div>
            <h3>Pending KYC</h3>
            <div className="value">{stats?.pendingKyc || 0}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        <div className="glass" style={{ padding: '24px' }}>
          <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: 600 }}>Revenue Overview</h3>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.revenueData || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a3241" vertical={false} />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px' }}
                />
                <Bar dataKey="amount" fill="var(--accent)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass" style={{ padding: '24px' }}>
          <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: 600 }}>Recent Activity</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {(stats?.recentUsers || []).map((user) => (
              <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)' }}></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', marginBottom: '4px' }}>{user.firstName} {user.lastName} registered</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{new Date(user.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
